import { useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { cn } from "lib/utils";
import {
  Building2,
  Check,
  ChevronRight,
  Clock,
  CreditCard,
  Loader2,
  MapPin,
  Shield,
  Store,
} from "lucide-react";

import { Button } from "js/components/ui/button";

const CARD_MAX_AMOUNT = 84_295;
const PAYNET_MAX_AMOUNT = 29_999;

type MethodKey = "card" | "store" | "bank_account";

interface PaymentMethodSelectorProps {
  onSelect: (method: "card" | "store" | "bank_account") => void;
  loading: boolean;
  amount?: number;
}

interface MethodDef {
  key: MethodKey;
  label: string;
  briefDesc: string;
  icon: typeof CreditCard;
  maxAmount?: number;
}

const methods: MethodDef[] = [
  {
    key: "card",
    label: "Tarjeta de crédito o débito",
    briefDesc: "Pago inmediato",
    icon: CreditCard,
    maxAmount: CARD_MAX_AMOUNT,
  },
  {
    key: "bank_account",
    label: "Transferencia SPEI",
    briefDesc: "Sin comisión · Acreditación en minutos",
    icon: Building2,
  },
  {
    key: "store",
    label: "Pago en efectivo (Paynet)",
    briefDesc: "Más de 25,000 puntos de venta",
    icon: Store,
    maxAmount: PAYNET_MAX_AMOUNT,
  },
];

function CardExpandedContent() {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <Shield className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">
          Pago seguro · Procesado por Openpay
        </span>
      </div>
      <div className="flex gap-2">
        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium dark:bg-slate-800">
          Visa
        </span>
        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium dark:bg-slate-800">
          Mastercard
        </span>
        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium dark:bg-slate-800">
          Amex
        </span>
      </div>
    </div>
  );
}

function BankExpandedContent() {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <Check className="h-3.5 w-3.5 text-green-600" />
        <span className="text-xs text-muted-foreground">
          Sin comisión bancaria
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">
          Acreditación en minutos durante horario bancario
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Recibirás instrucciones con CLABE y referencia.
      </p>
    </div>
  );
}

function StoreExpandedContent() {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">
          Disponible en más de 25,000 puntos de venta
        </span>
      </div>
      <a
        href="https://www.paynet.com.mx"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
      >
        Ver todas las tiendas afiliadas
        <ChevronRight className="size-3" />
      </a>
    </div>
  );
}

const expandedContentMap: Record<MethodKey, React.FC> = {
  card: CardExpandedContent,
  bank_account: BankExpandedContent,
  store: StoreExpandedContent,
};

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  onSelect,
  loading,
  amount,
}) => {
  const [selected, setSelected] = useState<MethodKey | undefined>(undefined);

  const isMethodDisabled = (method: MethodDef) => {
    if (amount && method.maxAmount && amount > method.maxAmount) {
      return true;
    }
    return false;
  };

  const handleContinue = () => {
    if (selected) {
      onSelect(selected);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-semibold text-foreground">
        Selecciona un método de pago
      </h3>

      <div className="flex flex-col gap-3">
        {methods.map((method) => {
          const disabled = isMethodDisabled(method);
          const isSelected = selected === method.key;
          const Icon = method.icon;
          const ExpandedContent = expandedContentMap[method.key];

          return (
            <div
              key={method.key}
              role="radio"
              aria-checked={isSelected}
              aria-disabled={disabled}
              tabIndex={disabled ? -1 : 0}
              onClick={() => !disabled && setSelected(method.key)}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  if (!disabled) setSelected(method.key);
                }
              }}
              className={cn(
                "relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200",
                isSelected
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/40 hover:bg-muted/30",
                disabled &&
                  "cursor-not-allowed opacity-50 hover:border-border hover:bg-transparent",
              )}
            >
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                    isSelected
                      ? "border-primary"
                      : "border-muted-foreground/40",
                  )}
                >
                  {isSelected && (
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  )}
                </div>

                <div
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors",
                    isSelected
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    {method.label}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {disabled
                      ? `No disponible para montos mayores a $${method.maxAmount?.toLocaleString("es-MX")}`
                      : method.briefDesc}
                  </p>
                </div>
              </div>

              <AnimatePresence initial={false}>
                {isSelected && !disabled && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="mt-3 border-t border-primary/10 pt-3">
                      <ExpandedContent />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      <Button
        className="mt-2 h-11 w-full text-base font-semibold"
        disabled={!selected || loading}
        onClick={handleContinue}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Procesando...
          </>
        ) : (
          "Continuar"
        )}
      </Button>
    </div>
  );
};

export default PaymentMethodSelector;
